import { useState, FC } from 'react';
import { Stack } from '@mui/material';
import type { Reference, Rubric } from '../../../../../../core/domain/Roaddata';
import FilterReference from './FilterReference';
import { FilterRubricOpen, StyledFilterReference } from './styles';
import { useAppDispatch, useAppSelector } from '../../../../../../types';
import { referenceToDisplayIds } from '../../../../../../core/filters/queries';
import { AddReferencesToDisplayIdCommand } from '../../../../../../core/filters/commands/AddReferencesToDisplayIdCommand';
import { RemoveReferencesToDisplayIdCommand } from '../../../../../../core/filters/commands/RemoveReferencesToDisplayIdCommand';
import { AddRubricsToDisplayIdCommand } from '../../../../../../core/filters/commands/AddRubricToDisplayIdCommand';
import { RemoveRubricsToDisplayIdCommand } from '../../../../../../core/filters/commands/RemoveRubricToDisplayIdCommand';
import BaseCheckMarkIcon from '../../../../../../assets/icons/checkmark.svg?react';
import { theme } from '../../../../style/theme';

const CheckMarkIcon = () => <BaseCheckMarkIcon color={theme.color.white.main} />;

type FilterReferenceGroupProps = {
    parent: Reference;
    children: Reference[];
    rubric: Rubric;
};

const FilterReferenceGroup: FC<FilterReferenceGroupProps> = ({ parent, children, rubric }) => {
    const dispatch = useAppDispatch();
    const referenceToDisplay = useAppSelector(referenceToDisplayIds);
    const [expanded, setExpanded] = useState(false);

    const sortedChildren = [...children].sort((a, b) => a.key.localeCompare(b.key));

    const hasChildren = sortedChildren.length > 0;
    const allChildrenSelected = hasChildren && sortedChildren.every((c) => referenceToDisplay.includes(c.id));
    const parentSelected = hasChildren ? allChildrenSelected : referenceToDisplay.includes(parent.id);

    const handleParentClick = () => {
        if (!hasChildren) {
            return;
        }
        const childIds = sortedChildren.map((c) => c.id);
        if (allChildrenSelected) {
            dispatch(RemoveReferencesToDisplayIdCommand(childIds));
            const otherRubricRefIds = rubric.references.filter((r) => !childIds.includes(r.id)).map((r) => r.id);
            const anyOtherSelected = referenceToDisplay.some((id) => otherRubricRefIds.includes(id));
            if (!anyOtherSelected) {
                dispatch(RemoveRubricsToDisplayIdCommand(rubric.id));
            }
        } else {
            dispatch(AddReferencesToDisplayIdCommand(childIds));
            dispatch(AddRubricsToDisplayIdCommand(rubric.id));
        }
    };

    return (
        <>
            <Stack direction="row" alignItems="center" sx={{ position: 'relative' }}>
                {hasChildren && (
                    <FilterRubricOpen onClick={() => setExpanded(!expanded)}>
                        {expanded ? '-' : '+'}
                    </FilterRubricOpen>
                )}
                {hasChildren ? (
                    <StyledFilterReference selected={parentSelected} onClick={handleParentClick}>
                        {parent.name}
                        {parentSelected && <CheckMarkIcon />}
                    </StyledFilterReference>
                ) : (
                    <FilterReference reference={parent} rubric={rubric} />
                )}
            </Stack>
            {expanded &&
                sortedChildren.map((child) => (
                    <FilterReference key={child.id} reference={child} rubric={rubric} indented />
                ))}
        </>
    );
};

export default FilterReferenceGroup;
